import React, { useState, useEffect } from 'react';
import { getTenantId, getApiKey } from '../utils/storage';

interface CredentialsViewProps {
    isOpen: boolean;
    onClose: () => void;
    onBack: () => void;
}

const CredentialsView: React.FC<CredentialsViewProps> = ({ isOpen, onClose, onBack }) => {
    const [tenantId, setTenantId] = useState<string | null>(null);
    const [apiKey, setApiKey] = useState<string | null>(null);
    const [showApiKey, setShowApiKey] = useState(false);
    const [copiedTenant, setCopiedTenant] = useState(false);
    const [copiedKey, setCopiedKey] = useState(false);

    useEffect(() => {
        if (isOpen) {
            setTenantId(getTenantId());
            setApiKey(getApiKey());
        }
    }, [isOpen]);

    const handleCopy = (text: string, isTenant: boolean) => {
        navigator.clipboard.writeText(text);
        if (isTenant) {
            setCopiedTenant(true);
            setTimeout(() => setCopiedTenant(false), 2000);
        } else {
            setCopiedKey(true);
            setTimeout(() => setCopiedKey(false), 2000);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white dark:bg-slate-800 rounded-lg w-full max-w-lg shadow-xl border border-slate-200 dark:border-slate-700 flex flex-col">
                {/* Header */}
                <div className="flex justify-between items-center p-6 border-b border-slate-200 dark:border-slate-700">
                    <div className="flex items-center space-x-3">
                        <button
                            onClick={onBack}
                            className="p-1 rounded-full hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
                        >
                            <span className="material-symbols-outlined text-slate-500 dark:text-slate-400" style={{ fontSize: '20px' }}>
                                arrow_back
                            </span>
                        </button>
                        <h2 className="text-xl font-bold text-slate-800 dark:text-slate-100">My Credentials</h2>
                    </div>
                    <button
                        onClick={onClose}
                        className="text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200 p-1 rounded-full hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
                    >
                        <span className="material-symbols-outlined" style={{ fontSize: '20px' }}>
                            close
                        </span>
                    </button>
                </div>

                {/* Content */}
                <div className="p-6 space-y-6">
                    <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-100 dark:border-blue-800 rounded-lg p-4">
                        <p className="text-sm text-blue-800 dark:text-blue-200">
                            Use these credentials to log in from other devices or browsers. Keep your API Key secret!
                        </p>
                    </div>

                    {/* Tenant ID */}
                    <div className="space-y-2">
                        <label className="text-sm font-semibold text-slate-700 dark:text-slate-300">
                            Tenant ID
                        </label>
                        <div className="flex items-center space-x-2">
                            <div className="flex-1 bg-slate-100 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-lg px-4 py-2 font-mono text-sm text-slate-600 dark:text-slate-400">
                                {tenantId || 'Not found'}
                            </div>
                            {tenantId && (
                                <button
                                    onClick={() => handleCopy(tenantId, true)}
                                    className="p-2 text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors"
                                    title="Copy Tenant ID"
                                >
                                    <span className="material-symbols-outlined" style={{ fontSize: '20px' }}>
                                        {copiedTenant ? 'check' : 'content_copy'}
                                    </span>
                                </button>
                            )}
                        </div>
                    </div>

                    {/* API Key */}
                    <div className="space-y-2">
                        <label className="text-sm font-semibold text-slate-700 dark:text-slate-300">
                            API Key
                        </label>
                        <div className="flex items-center space-x-2">
                            <div className="flex-1 bg-slate-100 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-lg px-4 py-2 font-mono text-sm text-slate-600 dark:text-slate-400 relative overflow-hidden">
                                {apiKey ? (showApiKey ? apiKey : '••••••••••••••••••••••••••••••••') : 'Not saved on this device'}
                            </div>
                            {apiKey && (
                                <>
                                    <button
                                        onClick={() => setShowApiKey(!showApiKey)}
                                        className="p-2 text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors"
                                        title={showApiKey ? "Hide API Key" : "Show API Key"}
                                    >
                                        <span className="material-symbols-outlined" style={{ fontSize: '20px' }}>
                                            {showApiKey ? 'visibility_off' : 'visibility'}
                                        </span>
                                    </button>
                                    <button
                                        onClick={() => handleCopy(apiKey, false)}
                                        className="p-2 text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors"
                                        title="Copy API Key"
                                    >
                                        <span className="material-symbols-outlined" style={{ fontSize: '20px' }}>
                                            {copiedKey ? 'check' : 'content_copy'}
                                        </span>
                                    </button>
                                </>
                            )}
                        </div>
                        {!apiKey && (
                            <p className="text-xs text-orange-600 dark:text-orange-400 mt-1">
                                Note: API Key is only saved after a fresh login or creation. If missing, please re-login.
                            </p>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default CredentialsView;
